import moment from 'moment';

/** Data civil (sem hora) — não aplicar conversão de fuso. */
const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** Instant absoluto com Z ou offset numérico. */
const HAS_EXPLICIT_OFFSET = /(?:[Zz]|[+-]\d{2}:?\d{2})$/;

/** Parte de calendário de um ISO, ignorando hora e offset. */
const ISO_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

export const FORMATO_DATA = 'DD/MM/YYYY';
export const FORMATO_DATA_HORA = 'DD/MM/YYYY HH:mm';

/**
 * Convenção do Portal para datas vindas das APIs (.NET):
 *
 * - Com `Z` ou `±HH:mm` → instante absoluto → exibe no fuso do navegador.
 * - Só `YYYY-MM-DD` → data civil, sem shift de fuso.
 * - ISO com hora **sem** offset (DateTime Unspecified) → horário de parede já
 *   gravado no servidor (Brasil). Exibe como está. **Não** usar `utc().local()`,
 *   que atrasa 3h no fuso de Brasília.
 *
 * @param {string|Date|number|null|undefined} value
 * @param {{ assumeUtcIfNoOffset?: boolean }} [options]
 * @returns {moment.Moment}
 */
export function parseApiDateTime(value, options = {}) {
    if (value == null || value === '') return moment.invalid();
    if (moment.isMoment(value)) return value.clone();
    if (value instanceof Date || typeof value === 'number') return moment(value);

    const s = String(value).trim();
    if (!s) return moment.invalid();

    if (ISO_DATE_ONLY.test(s)) {
        return moment(s, 'YYYY-MM-DD', true);
    }

    if (HAS_EXPLICIT_OFFSET.test(s)) {
        return moment(s);
    }

    if (options.assumeUtcIfNoOffset) {
        return moment.utc(s).local();
    }

    return moment(s);
}

/**
 * @param {string|Date|number|null|undefined} value
 * @param {string} [format]
 * @param {{ assumeUtcIfNoOffset?: boolean, empty?: string }} [options]
 */
export function formatarDataHora(value, format = FORMATO_DATA_HORA, options = {}) {
    const parsed = parseApiDateTime(value, options);
    if (!parsed.isValid()) return options.empty ?? '';
    return parsed.format(format);
}

/**
 * @param {string|Date|number|null|undefined} value
 * @param {string} [format]
 * @param {{ assumeUtcIfNoOffset?: boolean, empty?: string }} [options]
 */
export function formatarData(value, format = FORMATO_DATA, options = {}) {
    return formatarDataHora(value, format, options);
}

/**
 * Dias úteis (segunda a sexta) entre dois instantes: conta os dias posteriores ao
 * primeiro até o segundo, inclusive. Mesmo dia → 0; quinta → sexta → 1;
 * sexta → segunda → 1; sexta → terça → 2.
 *
 * Feriados não entram na conta — o front não tem calendário de feriados.
 *
 * @param {string|Date|number|moment.Moment|null|undefined} dataInicial
 * @param {string|Date|number|moment.Moment|null|undefined} dataFinal
 * @returns {number} nunca negativo
 */
export function diasUteisEntre(dataInicial, dataFinal) {
    const inicio = parseApiDateTime(dataInicial);
    const fim = parseApiDateTime(dataFinal);
    if (!inicio.isValid() || !fim.isValid()) return 0;

    const primeiroDia = inicio.clone().startOf('day');
    const ultimoDia = fim.clone().startOf('day');

    const totalDias = ultimoDia.diff(primeiroDia, 'days');
    if (totalDias <= 0) return 0;

    // Toda semana cheia tem exatamente 5 dias úteis; só a sobra precisa ser varrida.
    const semanasCompletas = Math.floor(totalDias / 7);
    let diasUteis = semanasCompletas * 5;

    const cursor = primeiroDia.clone().add(semanasCompletas * 7, 'days');
    while (cursor.isBefore(ultimoDia)) {
        cursor.add(1, 'day');
        const diaDaSemana = cursor.day();
        if (diaDaSemana !== 0 && diaDaSemana !== 6) diasUteis += 1;
    }

    return diasUteis;
}

/**
 * Data civil para campos que a API expõe como `DateTime` vindo de coluna `DATE`.
 *
 * O serializador do .NET marca esse valor como UTC (`2026-08-10T00:00:00.0000000Z`);
 * lido como instante, atrasa um dia em fuso negativo — 10/08 exibe 09/08 em Brasília.
 * Vale só a parte de calendário, porque data civil não tem fuso. Para instantes de
 * verdade (`dataSolicitacao`, histórico), use `parseApiDateTime`.
 *
 * @param {string|Date|number|moment.Moment|null|undefined} value
 * @returns {Date|null} meia-noite local, ou null se não der para interpretar
 */
export function parseDataCivil(value) {
    if (value == null || value === '') return null;

    if (typeof value === 'string') {
        const [, ano, mes, dia] = ISO_DATE_PREFIX.exec(value.trim()) || [];
        return ano ? new Date(Number(ano), Number(mes) - 1, Number(dia)) : null;
    }

    const d = moment.isMoment(value) ? value.toDate() : new Date(value);
    return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * @param {string|Date|number|null|undefined} value
 * @param {string} [format]
 * @param {{ empty?: string }} [options]
 */
export function formatarDataCivil(value, format = FORMATO_DATA, options = {}) {
    const data = parseDataCivil(value);
    if (!data) return options.empty ?? '';
    return moment(data).format(format);
}

/**
 * Serializa uma data civil para `AAAA-MM-DD`. `toISOString().slice(0, 10)` não serve:
 * converte para UTC antes de cortar, trocando o dia em fusos positivos.
 *
 * @param {string|Date|number|null|undefined} value
 * @returns {string|null}
 */
export function formatarDataCivilIso(value) {
    const data = parseDataCivil(value);
    return data ? moment(data).format('YYYY-MM-DD') : null;
}

/**
 * Compatível com o antigo `$dateFormat(value, format)`.
 * @param {string|Date|number|null|undefined} value
 * @param {string} [format]
 */
export default function dateFormat(value, format = FORMATO_DATA) {
    if (!value) return '';
    return formatarDataHora(value, format);
}
